import React, { useState } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  SelectChangeEvent,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useQuery, useMutation } from "@apollo/client";
import { GET_WALLETS, CREATE_WALLET, UPDATE_WALLET } from "../graphql/wallets";

interface Wallet {
  id: string;
  name: string;
  type: string;
  balance: number;
  currency: string;
  description?: string;
}

interface WalletSelectorProps {
  selectedWalletId: string | null;
  onWalletChange: (walletId: string | null) => void;
}

const WalletSelector: React.FC<WalletSelectorProps> = ({
  selectedWalletId,
  onWalletChange,
}) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [newWalletData, setNewWalletData] = useState({
    name: "",
    type: "SPOT",
    balance: 0,
    currency: "USD",
    description: "",
  });

  const { data: walletsData, refetch } = useQuery(GET_WALLETS);
  const [createWallet, { data: createData }] = useMutation(CREATE_WALLET);
  const [updateWallet, { data: updateData }] = useMutation(UPDATE_WALLET);

  // Handle mutation results via effects instead of onCompleted
  React.useEffect(() => {
    if (createData?.createWallet) {
      refetch();
      setIsCreateDialogOpen(false);
      setNewWalletData({
        name: "",
        type: "SPOT",
        balance: 0,
        currency: "USD",
        description: "",
      });
    }
  }, [createData, refetch]);

  React.useEffect(() => {
    if (updateData?.updateWallet) {
      refetch();
      setIsEditDialogOpen(false);
    }
  }, [updateData, refetch]);

  const wallets = walletsData?.wallets || [];

  // Auto-select first wallet if available and none selected
  React.useEffect(() => {
    if (!selectedWalletId && wallets.length > 0) {
      onWalletChange(wallets[0].id);
    }
  }, [wallets, selectedWalletId, onWalletChange]);

  const handleWalletChange = (event: SelectChangeEvent<string>) => {
    const walletId = event.target.value;
    onWalletChange(walletId === "none" ? null : walletId);
  };

  const handleCreateWallet = () => {
    createWallet({
      variables: {
        createWalletInput: newWalletData,
      },
    });
  };

  const handleEditWallet = () => {
    if (selectedWalletId) {
      const selectedWallet = wallets.find(
        (w: Wallet) => w.id === selectedWalletId,
      );
      if (selectedWallet) {
        setNewWalletData({
          name: selectedWallet.name,
          type: selectedWallet.type,
          balance: selectedWallet.balance,
          currency: selectedWallet.currency,
          description: selectedWallet.description || "",
        });
        setIsEditDialogOpen(true);
      }
    }
  };

  const handleUpdateWallet = () => {
    if (selectedWalletId) {
      updateWallet({
        variables: {
          id: selectedWalletId,
          updateWalletInput: newWalletData,
        },
      });
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="body2" color="inherit">
          Wallet:
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Select Wallet</InputLabel>
          <Select
            value={
              selectedWalletId && wallets.length > 0 ? selectedWalletId : "none"
            }
            label="Select Wallet"
            onChange={handleWalletChange}
          >
            <MenuItem value="none">Select a Wallet</MenuItem>
            {wallets.map((wallet: Wallet) => (
              <MenuItem key={wallet.id} value={wallet.id}>
                {wallet.name} (${wallet.balance.toLocaleString()})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setIsCreateDialogOpen(true)}
          sx={{ color: "inherit", borderColor: "inherit" }}
        >
          + New Wallet
        </Button>
        {selectedWalletId && (
          <IconButton
            size="small"
            onClick={handleEditWallet}
            sx={{ color: "inherit" }}
          >
            <EditIcon />
          </IconButton>
        )}
      </Box>

      <Dialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
      >
        <DialogTitle>Create New Wallet</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Wallet Name"
            fullWidth
            variant="outlined"
            value={newWalletData.name}
            onChange={(e) =>
              setNewWalletData({ ...newWalletData, name: e.target.value })
            }
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Type</InputLabel>
            <Select
              value={newWalletData.type}
              label="Type"
              onChange={(e) =>
                setNewWalletData({ ...newWalletData, type: e.target.value })
              }
            >
              <MenuItem value="SPOT">Spot</MenuItem>
              <MenuItem value="FUTURES">Futures</MenuItem>
              <MenuItem value="MARGIN">Margin</MenuItem>
              <MenuItem value="OPTIONS">Options</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Initial Balance"
            type="number"
            fullWidth
            variant="outlined"
            value={newWalletData.balance}
            onChange={(e) =>
              setNewWalletData({
                ...newWalletData,
                balance: parseFloat(e.target.value) || 0,
              })
            }
          />
          <TextField
            margin="dense"
            label="Currency"
            fullWidth
            variant="outlined"
            value={newWalletData.currency}
            onChange={(e) =>
              setNewWalletData({ ...newWalletData, currency: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            variant="outlined"
            value={newWalletData.description}
            onChange={(e) =>
              setNewWalletData({
                ...newWalletData,
                description: e.target.value,
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateWallet} variant="contained">
            Create Wallet
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
      >
        <DialogTitle>Edit Wallet</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Wallet Name"
            fullWidth
            variant="outlined"
            value={newWalletData.name}
            onChange={(e) =>
              setNewWalletData({ ...newWalletData, name: e.target.value })
            }
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Type</InputLabel>
            <Select
              value={newWalletData.type}
              label="Type"
              onChange={(e) =>
                setNewWalletData({ ...newWalletData, type: e.target.value })
              }
            >
              <MenuItem value="SPOT">Spot</MenuItem>
              <MenuItem value="FUTURES">Futures</MenuItem>
              <MenuItem value="MARGIN">Margin</MenuItem>
              <MenuItem value="OPTIONS">Options</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Balance"
            type="number"
            fullWidth
            variant="outlined"
            value={newWalletData.balance}
            onChange={(e) =>
              setNewWalletData({
                ...newWalletData,
                balance: parseFloat(e.target.value) || 0,
              })
            }
          />
          <TextField
            margin="dense"
            label="Currency"
            fullWidth
            variant="outlined"
            value={newWalletData.currency}
            onChange={(e) =>
              setNewWalletData({ ...newWalletData, currency: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            variant="outlined"
            value={newWalletData.description}
            onChange={(e) =>
              setNewWalletData({
                ...newWalletData,
                description: e.target.value,
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateWallet} variant="contained">
            Update Wallet
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WalletSelector;
